import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "react-phone-number-input/style.css";
import { Message } from "primereact/message";
import PhoneInput from "react-phone-number-input";
import { Toast } from "primereact/toast";
import { paymentLinkService } from "../../../services/paymentLinkService";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Loader } from "../../common/LoaderPaymentLink";

const POLL_INTERVAL_MS = 5_000;   // interroge toutes les 5 secondes
const POLL_TIMEOUT_MS  = 300_000; // abandonne après 5 minutes

const PaymentLink: React.FC = () => {
  const [selected, setSelected] = useState<string | null>(null);
  const toastRef = useRef<Toast>(null);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const { id_link } = useParams<{ id_link: string }>();
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Refs pour pouvoir arrêter le polling depuis n'importe quel contexte
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef  = useRef<ReturnType<typeof setTimeout>  | null>(null);

  /** Arrête proprement interval + timeout du polling */
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  // Nettoyage au démontage du composant
  useEffect(() => () => stopPolling(), []);

  const handleSelect = async (operator: { code: string; id: string }) => {
    setSelected(operator.code);
    await formik.setFieldValue("serviceId", operator.id, false);
    await formik.setFieldValue("selected", operator.code, false);
    await formik.setFieldTouched("selected", true, false);
    await formik.setFieldTouched("serviceId", true, false);
    await formik.validateField("selected");
    await formik.validateField("serviceId");
  };

  const fetchPaymentLinks = async () => {
    if (!id_link) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Payment ID is missing",
        life: 3000,
      });
      return;
    }

    const res = await paymentLinkService.initPayment(id_link);

    if (res?.status === -1 || res?.code === "PAYMENT_LINK_EXPIRED") {
      toastRef.current?.show({
        severity: "warn",
        summary: "Operation failed",
        detail: res?.message || "Payment link has expired",
        life: 3000,
      });
      setFetchError(res?.message || "Link expired or invalid");
      return;
    }

    if (res?.status !== 2000 || !res?.data) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: res?.message || "Failed to fetch payment data",
        life: 3000,
      });
      setFetchError(res?.message || "Unable to load payment data");
      return;
    }

    setPaymentData(res.data);
  };

  /**
   * Lance un polling sur le statut de la transaction.
   * S'arrête dès que le statut est SUCCESS ou FAILED, ou après POLL_TIMEOUT_MS.
   */
  const startPollingStatus = () => {
    // Timeout global : si le polling n'a pas conclu dans le délai max, on abandonne
    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      toastRef.current?.show({
        severity: "warn",
        summary: "Timeout",
        detail: "Payment status could not be confirmed in time. Please check later.",
        life: 5000,
      });
      setSubmitting(false);
      setIsCheckingStatus(false);
    }, POLL_TIMEOUT_MS);

    // Interrogation périodique
    pollIntervalRef.current = setInterval(async () => {
      const statusResponse = await paymentLinkService.PaymentLinkCheckStatus(id_link!);

      // Erreur réseau / API sur le check de statut
      if (statusResponse?.status === -1) {
        stopPolling();
        toastRef.current?.show({
          severity: "warn",
          summary: "Status Check Failed",
          detail: statusResponse?.message || "Unable to check payment status",
          life: 3000,
        });
        setSubmitting(false);
        setIsCheckingStatus(false);
        return;
      }

      const transactionStatus = statusResponse?.data?.transaction?.status;

      if (transactionStatus === "SUCCESS") {
        stopPolling();
        toastRef.current?.show({
          severity: "success",
          summary: "Payment Successful",
          detail: statusResponse?.message || "Your payment was processed successfully.",
          life: 4000,
        });
        setSubmitting(false);
        setIsCheckingStatus(false);
        return;
      }

      if (transactionStatus === "FAILED") {
        stopPolling();
        toastRef.current?.show({
          severity: "error",
          summary: "Transaction Failed",
          detail: statusResponse?.message || "Your transaction has failed. Please try again.",
          life: 4000,
        });
        setSubmitting(false);
        setIsCheckingStatus(false);
        return;
      }

      // Statut encore PENDING → on continue le polling (pas d'action)
    }, POLL_INTERVAL_MS);
  };

  const formik = useFormik({
    initialValues: {
      serviceId: "",
      phoneNumber: "",
      selected: "",
    },
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      serviceId: Yup.string().required("Service is required"),
      phoneNumber: Yup.string()
          .required("Phone number is required")
          .matches(/^\+\d{6,15}$/, "Phone number format is invalid"),
      selected: Yup.string().required("Please select a payment method"),
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      setIsCheckingStatus(true);

      // 1. Initialise la transaction
      const initPayment = await paymentLinkService.initPaymentLinkTransaction({
        serviceId: values.serviceId,
        phoneNumber: values.phoneNumber,
        linkId: id_link!,
      });

      if (initPayment?.status === -1 || initPayment?.code === "PAYMENT_LINK_EXPIRED") {
        toastRef.current?.show({
          severity: "warn",
          summary: "Transaction failed",
          detail: initPayment?.message || "Payment link has expired",
          life: 3000,
        });
        setSubmitting(false);
        setIsCheckingStatus(false);
        return;
      }

      if (initPayment?.status !== 2000) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Transaction failed",
          detail: initPayment?.message || "Failed to initiate payment",
          life: 3000,
        });
        setSubmitting(false);
        setIsCheckingStatus(false);
        return;
      }

      // 2. Transaction initialisée → démarrer le polling du statut
      toastRef.current?.show({
        severity: "info",
        summary: "Payment Processing",
        detail: "Transaction initiated. Waiting for confirmation…",
        life: 4000,
      });

      startPollingStatus();
    },
  });

  useEffect(() => {
    fetchPaymentLinks();
  }, []);

  if (fetchError) {
    return (
        <div className="flex flex-col justify-center items-center h-screen text-center">
          <Toast ref={toastRef} className="toast-top-center" />
          <p className="text-red-500 text-lg mb-4">{fetchError}</p>
          <Button label="Retry" onClick={fetchPaymentLinks} icon="pi pi-refresh" />
        </div>
    );
  }

  if (!paymentData) {
    return <Loader message="Loading payment link..." />;
  }

  return (
      <div className="h-screen flex justify-center items-center mt-4">
        <Toast ref={toastRef} className="toast-top-center" />
        {isCheckingStatus && <Loader />}
        <div className="flex flex-col sm:flex-row justify-between gap-5">
          <div className="bg-white rounded-lg shadow-lg w-96 p-6 text-center">
            <p className="mb-2">
              You are about to pay <strong>{paymentData.merchantName}</strong> (the merchant collecting this payment).
            </p>
            <p className="text-sm">Please review your payment information below before proceeding.</p>
            <div className="payment-info">
              <table className="mb-6 w-full border border-gray-300">
                <thead>
                <tr className="bg-gray-200">
                  <th className="border border-gray-300 p-2" colSpan={2}>
                    Payment information
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr className="border border-gray-300">
                  <td className="border border-gray-300 p-2">Amount</td>
                  <td className="border border-gray-300 p-2">
                    {paymentData.amount} {paymentData.currency}
                  </td>
                </tr>
                <tr className="border border-gray-300">
                  <td className="border border-gray-300 p-2">Total to pay</td>
                  <td className="border border-gray-300 p-2">
                    {paymentData.amount} {paymentData.currency}
                  </td>
                </tr>
                <tr className="border border-gray-300">
                  <td className="border border-gray-300 p-2">Reason</td>
                  <td className="border border-gray-300 p-2">{paymentData.reason}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg w-96 p-6 text-center">
            <p className="mb-2">Select your payment method to proceed to payment</p>
            <form onSubmit={formik.handleSubmit}>
              <div className="payment-method">
                <div className="grid grid-cols-3 gap-4">
                  {paymentData.services.map((operator: any, index: number) => (
                      <div
                          key={index}
                          className={`w-24 h-24 border-4 rounded-md cursor-pointer overflow-hidden flex justify-center items-center transition-all ${
                              selected === operator.code ? "border-blue-600 shadow-lg" : "border-gray-600"
                          }`}
                          onClick={() => handleSelect(operator)}
                      >
                        <img
                            src={`/assets/images/services/${operator.code}.png`}
                            alt={operator.name}
                            className="object-cover w-full h-full"
                        />
                      </div>
                  ))}
                </div>
                {formik.errors.selected && formik.touched.selected && (
                    <Message severity="error" text={formik.errors.selected} className="input-message" />
                )}
              </div>
              <div className="field mt-3 col-span-2 sm:grid-cols-2 md:grid-cols-3">
                <PhoneInput
                    placeholder="Enter phone number"
                    international
                    countryCallingCodeEditable={false}
                    id="phoneNumber"
                    name="phoneNumber"
                    defaultCountry={(paymentData.customerCountry || "CM").toUpperCase()}
                    countries={[(paymentData.customerCountry || "CM").toUpperCase()]}
                    className={`w-full p-2 border rounded-md ${
                        formik.errors.phoneNumber && formik.touched.phoneNumber
                            ? "border-red-500"
                            : "border-gray-300"
                    }`}
                    value={formik.values.phoneNumber}
                    onBlur={formik.handleBlur}
                    onChange={(phoneNumber) => {
                      formik.setFieldValue("phoneNumber", phoneNumber || "", true);
                      formik.setFieldTouched("phoneNumber", true, true);
                    }}
                />
                {formik.errors.phoneNumber && formik.touched.phoneNumber && (
                    <Message severity="error" text={formik.errors.phoneNumber} className="input-message" />
                )}
              </div>
              {!submitting && (
                  <Button
                      type="submit"
                      label="Submit"
                      loading={submitting}
                      disabled={
                          submitting ||
                          !formik.isValid ||
                          !formik.values.serviceId ||
                          !formik.values.selected ||
                          !formik.values.phoneNumber
                      }
                      icon="pi pi-pay"
                      className={`w-1/2 text-white py-2 mt-2 rounded-md flex items-center justify-center gap-2 transition-all mx-auto ${
                          submitting ||
                          !formik.isValid ||
                          !formik.values.serviceId ||
                          !formik.values.selected ||
                          !formik.values.phoneNumber
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-primary-600 hover:bg-primary-700"
                      }`}
                  />
              )}
            </form>
          </div>
        </div>
      </div>
  );
};

export default PaymentLink;